/**
 * WEB "Live Event" card — design_handoff_live_events_v11.
 *
 * A fixed-size landscape cell (332×164 mobile / 478×206 desktop, every internal
 * value ×1.26) of ultra-wide event key art with TWO scrims: a horizontal one that
 * darkens the left third (where all the type sits, keeping the art's right-side
 * subject clear) and a vertical one protecting the top identity row and the bottom
 * action row. A fixed height + a flex column ending in `items-end` cannot overflow
 * and clip the PLAY button, which the earlier `aspect-ratio` version did.
 *
 * The event art is EVENT branding, so the card names its game: an app-icon +
 * game-name identity row sits top-left; the event name, PRIZE POOL eyebrow and the
 * dominant green prize figure stack bottom-left; Entry/Ends + the partner-accent
 * PLAY button sit bottom-right, PLAY rightmost. Prize green (#3ddc97) and LIVE red
 * (#ff4d5e) are never-theme colors; only PLAY takes the partner accent.
 *
 * Tap opens the SAME route the native home tap does — /games/{gameId}/live-event/
 * {eventId} — which STARTS the event (the details screen cold-hydrates the
 * tournament from the id). {gameId} is the event's REAL game, not the shell, so this
 * is a genuine /games route (not part of the shell top-level route migration).
 * Carries the partner slug for co-brand (root is vanilla Skillz, no `?partner=`).
 * Falls back to the game home when an event id is missing. The whole card is the link.
 */

'use client';

import {useEffect, useState} from 'react';
import type {LiveEvent} from '@/services/partner/getLiveEvents';
import LiveEventCountdown from './LiveEventCountdown';

// Scrim 1 — horizontal: darkens the left where the type lives, clears to near-
// transparent on the right so the art's subject stays uncovered.
const SCRIM_H =
  'linear-gradient(90deg, rgba(6,6,12,0.94) 0%, rgba(6,6,12,0.84) 34%, rgba(6,6,12,0.42) 66%, rgba(6,6,12,0.10) 100%)';
// Scrim 2 — vertical: protects the top identity row and the bottom action row where
// small 9.5px type crosses brighter pixels.
const SCRIM_V =
  'linear-gradient(180deg, rgba(6,6,12,0.30) 0%, rgba(6,6,12,0) 32%, rgba(6,6,12,0.56) 72%, rgba(6,6,12,0.88) 100%)';

// "Never theme" colors (design §hierarchy): green money, red live.
const PRIZE_GREEN = '#3ddc97';
const LIVE_RED = '#ff4d5e';

const HEADING_FONT = 'var(--partner-heading-font, inherit)';
const TEXT_SHADOW = '0 1px 3px rgba(0,0,0,0.6)';

interface LiveEventCardProps {
  event: LiveEvent;
  slug: string;
  rootMode?: boolean;
}

export default function LiveEventCard({event, slug, rootMode}: LiveEventCardProps) {
  const start = new Date(event.startsAt).getTime();
  const end = new Date(event.endsAt).getTime();
  // Re-render exactly at the next phase boundary (start, then end) so the LIVE pill and the
  // Ends/Starts label flip on their own — LiveEventCountdown ticks independently and never
  // drove isLive, so a started event otherwise stayed stuck on "Starts". Timeout, not a tick.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = Date.now();
    const next = t < start ? start : t < end ? end : null;
    if (next == null) return;
    const timer = setTimeout(() => setNow(Date.now()), next - t + 50);
    return () => clearTimeout(timer);
  }, [start, end, now]);
  const isLive = now >= start && now < end;

  const path =
    event.eventId != null
      ? `/games/${event.gameId}/live-event/${encodeURIComponent(String(event.eventId))}`
      : `/games/${event.gameId}/`;
  const href = rootMode ? path : `${path}?partner=${encodeURIComponent(slug)}`;

  return (
    <a
      href={href}
      // Shadowless: the card's drop shadow (and its hover lift) was the thing the rail
      // dividers were trying to frame; with the depth treatment gone the shadow just read
      // as grime against the surface. Keep the translate on hover for feedback, drop the
      // shadow. The card's own corner radius is its original design and stays.
      className='group relative block h-[131px] w-[266px] overflow-hidden rounded-[11px] bg-[#08080e] no-underline transition-transform duration-200 hover:-translate-y-0.5 active:translate-y-px sm:h-[165px] sm:w-[382px] sm:rounded-[14px]'
    >
      {/* Key art fills the card: video preferred, image fallback. */}
      {event.videoUrl ? (
        <video
          className='absolute inset-0 h-full w-full object-cover'
          src={event.videoUrl}
          poster={event.imageUrl}
          autoPlay
          muted
          loop
          playsInline
          aria-hidden
        />
      ) : event.imageUrl ? (
        // eslint-disable-next-line @next/next/no-img-element -- remote event key art
        <img className='absolute inset-0 h-full w-full object-cover' src={event.imageUrl} alt='' />
      ) : null}

      <div className='absolute inset-0' style={{background: SCRIM_H}} />
      <div className='absolute inset-0' style={{background: SCRIM_V}} />

      {/* LIVE pill, top-right — over the art, not the type. */}
      {isLive ? (
        <div className='absolute right-2 top-2 flex items-center gap-1 rounded-full bg-[rgba(6,6,12,0.62)] px-[7px] py-[2px] sm:right-2.5 sm:top-2.5'>
          <span className='h-1 w-1 rounded-full' style={{backgroundColor: LIVE_RED}} />
          <span className='text-[7px] font-extrabold tracking-[0.1em] text-white sm:text-[8px]'>
            LIVE
          </span>
        </div>
      ) : null}

      {/* Content column: fills the card, ends in flex-end so it can't overflow. */}
      <div className='absolute inset-0 flex flex-col px-[10px] py-[9px] sm:px-3 sm:py-2.5'>
        {/* Row 1 — game identity, top-left. */}
        <div className='flex items-center gap-2 sm:gap-2.5'>
          {event.iconUrl ? (
            // eslint-disable-next-line @next/next/no-img-element -- remote game icon
            <img
              src={event.iconUrl}
              alt=''
              className='h-9 w-9 flex-none rounded-[10px] object-cover shadow-[0_3px_10px_rgba(0,0,0,0.6)] sm:h-11 sm:w-11 sm:rounded-[12px]'
            />
          ) : null}
          {event.gameName ? (
            <span
              className='max-w-[128px] truncate text-[12px] font-bold tracking-[-0.01em] text-white sm:max-w-[168px] sm:text-[15px]'
              style={{fontFamily: HEADING_FONT, textShadow: TEXT_SHADOW}}
            >
              {event.gameName}
            </span>
          ) : null}
        </div>

        {/* Row 2 — event/prize (left) + Entry/Ends + PLAY (right), bottom-aligned. */}
        <div className='flex flex-1 items-end justify-between gap-2 sm:gap-2.5'>
          <div className='min-w-0'>
            <div
              className='truncate text-[11px] font-bold tracking-[-0.01em] text-white sm:text-[14px]'
              style={{fontFamily: HEADING_FONT, textShadow: '0 1px 3px rgba(0,0,0,0.55)'}}
            >
              {event.title}
            </div>
            {event.prize ? (
              <>
                <div className='mt-0.5 text-[6px] font-extrabold uppercase leading-none tracking-[0.13em] text-white/[0.56] sm:text-[7px]'>
                  Prize Pool
                </div>
                <div
                  className='mt-[1px] text-[26px] font-extrabold leading-none tracking-[-0.03em] sm:text-[32px]'
                  style={{color: PRIZE_GREEN, fontFamily: HEADING_FONT}}
                >
                  {event.prize}
                </div>
              </>
            ) : null}
          </div>

          <div className='flex flex-none items-center gap-[7px] sm:gap-[9px]'>
            <div
              className='flex flex-col items-end whitespace-nowrap text-right text-[8px] font-bold leading-[1.3] text-white/[0.78] [font-variant-numeric:tabular-nums] sm:text-[10px]'
              style={{textShadow: '0 1px 2px rgba(0,0,0,0.6)'}}
            >
              {event.entryFee ? <span>Entry {event.entryFee}</span> : null}
              <span>
                {isLive ? 'Ends ' : 'Starts '}
                <LiveEventCountdown startsAt={event.startsAt} endsAt={event.endsAt} />
              </span>
            </div>
            <span
              className='flex-none rounded-[6px] px-[14px] py-1.5 text-[9px] font-extrabold uppercase tracking-[0.05em] sm:px-[18px] sm:py-2 sm:text-[11px]'
              style={{backgroundColor: 'var(--partner-accent)', color: 'var(--partner-accent-text)'}}
            >
              Play
            </span>
          </div>
        </div>
      </div>
    </a>
  );
}
